import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Support → Settings → Flows: every text, button and menu the WhatsApp / chat flows send, as a chart.
// Edit a draft, test it on your own WhatsApp, publish. Decisions and actions are shown but locked.
const LANGS = [['en', 'English'], ['hinglish', 'Hinglish'], ['hi', 'Hindi']];
const KIND = { trigger: 'Starts when', decision: 'Decision', message: 'Message', label: 'Label', buttons: 'Buttons', list: 'Menu', action: 'Action' };
const EDITABLE = new Set(['message', 'label', 'buttons', 'list']);
const SAMPLE = {
  name: 'Priya', next: '10 AM tomorrow', when: 'by 10 AM tomorrow', order: '#7521088', count: 'latest 3', courier: 'Delhivery',
  order_list: '• #7521088 · 2 fabrics · placed 12 Oct\n• #7519870 · 1 fabric · placed 5 Oct\n• #7501122 · 3 fabrics · placed 28 Sep',
  status: '📦 #7521088 was shipped on 12 Oct with Delhivery. Expected delivery 15 Oct–17 Oct.', order_link: 'fvs-workspace.web.app/r/o/K7x9Q2',
  reason: 'delivery taking too long', topic: 'payment or refund', about_order: ' about order #7521088', stage: 'Shipped',
  stage_done: 'has already been shipped', photo_hint: ' with a measuring tape', late_note: ' Our team will check it by 10 AM tomorrow.',
};
const fill = (t) => Object.entries(SAMPLE).reduce((s, [k, v]) => s.split(`{${k}}`).join(v), String(t || ''));
const clone = (v) => JSON.parse(JSON.stringify(v ?? null));
const pick = (o, lang) => (o?.[lang] && String(o[lang]).trim() ? o[lang] : o?.en || '');
const when = (ms) => (ms ? new Date(ms).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '');

function Preview({ step, value, lang }) {
  if (!value) return null;
  if (step.kind === 'message' || step.kind === 'label') {
    return <div className="fl-phone"><div className="fl-bub">{fill(pick(value.text, lang)) || <i>Empty</i>}<small>9:41 pm ✓✓</small></div></div>;
  }
  const items = (value.items || []).filter((i) => i.enabled !== false);
  return (
    <div className="fl-phone">
      {step.kind === 'list'
        ? <div className="fl-btns"><span>☰ {pick(value.menuButton, lang) || 'Choose'}</span></div>
        : <div className="fl-btns">{items.map((i, n) => <span key={n}>{pick(i.title, lang)}</span>)}</div>}
      {step.kind === 'list' && <div className="fl-menu">{items.map((i, n) => <div key={n}>{pick(i.title, lang)}</div>)}</div>}
    </div>
  );
}

function Counter({ value, max }) {
  const n = Array.from(String(value || '')).length; // like WhatsApp: an emoji is one character
  return <span className={`fl-count${n > max ? ' over' : ''}`}>{n}/{max}</span>;
}

// New flows go out Off → Test (only the WhatsApp test number) → Live (everyone)
const FLOW_SWITCH = { calculator: 'fabricCalc', colour: 'colourHelp' };
const FLOW_SWITCH_HINT = { off: 'Off: nobody gets it', test: 'Test: only the test number gets it', live: 'Live: every customer gets it' };

function FlowsPage({ api }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [nodes, setNodes] = useState({}); // working copy: published + draft + local edits
  const [dirty, setDirty] = useState(new Set());
  const [flowId, setFlowId] = useState('track');
  const [stepId, setStepId] = useState(null);
  const [lang, setLang] = useState('en');
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await api('/api/v1/support/flows');
      setData(d);
      setNodes({ ...clone(d.published), ...clone(d.draft) });
      setDirty(new Set(Object.keys(d.draft || {})));
      setErr(null);
    } catch (e) { setErr(e.message); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const flow = useMemo(() => data?.flows.find((f) => f.id === flowId) || data?.flows[0], [data, flowId]);
  const step = flow?.steps.find((s) => s.id === stepId) || flow?.steps.find((s) => EDITABLE.has(s.kind)) || flow?.steps[0];
  const value = step ? nodes[step.id] : null;
  const edit = data?.canEdit;

  const change = (id, next) => { setNodes((p) => ({ ...p, [id]: next })); setDirty((p) => new Set(p).add(id)); setMsg(null); };
  const draftPayload = () => Object.fromEntries([...dirty].map((id) => [id, nodes[id]]));
  const flash = (ok, text) => setMsg({ ok, text });

  const saveDraft = async () => {
    setBusy('save');
    try { await api('/api/v1/support/flows/draft', 'PUT', { nodes: draftPayload() }); flash(true, 'Draft saved. Customers still see the published version.'); await load(); }
    catch (e) { flash(false, e.message); } finally { setBusy(null); }
  };
  const publish = async () => {
    setBusy('publish');
    try {
      if (dirty.size) await api('/api/v1/support/flows/draft', 'PUT', { nodes: draftPayload() });
      const r = await api('/api/v1/support/flows/publish', 'POST', { note });
      flash(true, `Published version ${r.version}. Customers see it within a minute.`); setNote(''); await load();
    } catch (e) { flash(false, e.message); } finally { setBusy(null); }
  };
  const test = async () => {
    setBusy('test');
    try {
      if (dirty.size) await api('/api/v1/support/flows/draft', 'PUT', { nodes: draftPayload() });
      const r = await api('/api/v1/support/flows/test', 'POST', { stepId: step.id, lang });
      flash(true, `Test sent to ${r.to}. Check your WhatsApp.`);
    } catch (e) { flash(false, e.message); } finally { setBusy(null); }
  };
  const revert = async (version) => {
    setBusy('revert');
    try { await api('/api/v1/support/flows/revert', 'POST', { version }); flash(true, `Version ${version} is now in the draft. Check it, then publish.`); setShowHistory(false); await load(); }
    catch (e) { flash(false, e.message); } finally { setBusy(null); }
  };
  const resetStep = () => change(step.id, clone(data.defaults[step.id]));
  const setFlowMode = async (key, mode) => {
    setBusy('mode');
    try { await api('/api/v1/support/flows/feature', 'POST', { key, mode }); setData((d) => ({ ...d, features: { ...(d.features || {}), [key]: mode } })); flash(true, FLOW_SWITCH_HINT[mode]); }
    catch (e) { flash(false, e.message); } finally { setBusy(null); }
  };

  if (err) return <section className="cx-panel"><div className="cx-panel-b"><span className="cx-err">{err}</span></div></section>;
  if (!data) return <section className="cx-panel"><div className="cx-skel" style={{ height: 320, margin: 16 }} /></section>;

  const usage = (s) => {
    const u = data.usage || {};
    if (s.id === 'after_hours.message') return u.after_hours;
    if (s.id === 'busy.message') return u.busy;
    if (s.kind === 'buttons') return s.items.reduce((t, i) => t + Object.entries(u).filter(([k]) => k.startsWith(`tap:${i.key}`)).reduce((a, [, v]) => a + v, 0), 0) || null;
    return null;
  };

  return (
    <section className="cx-panel fl" aria-labelledby="fl-h">
      <div className="cx-panel-h">
        <h2 id="fl-h">Chat flows</h2>
        <span className={`cx-chip ${dirty.size ? 'cx-c-waiting' : 'cx-c-resolved'}`}>
          {dirty.size ? `Draft · ${dirty.size} unpublished change${dirty.size === 1 ? '' : 's'}` : data.version ? `Published · version ${data.version}${data.publishedBy ? ` · ${data.publishedBy}` : ''}` : 'Using the original texts'}
        </span>
      </div>
      <div className="fl-bar">
        <span className="cx-hint">Edit what customers read and which options they get. Decisions, privacy checks and tasks stay fixed.{edit ? '' : ' Only admins and Support managers can change flows.'}</span>
        <span className="cx-spacer" />
        <button className="cx-btn cx-btn-sm" onClick={() => setShowHistory((v) => !v)} aria-expanded={showHistory}>Version history</button>
        {edit && <button className="cx-btn cx-btn-sm" onClick={saveDraft} disabled={!dirty.size || !!busy}>{busy === 'save' ? 'Saving…' : 'Save draft'}</button>}
        {edit && <button className="cx-btn cx-btn-sm" onClick={test} disabled={!step || !EDITABLE.has(step.kind) || !!busy}>{busy === 'test' ? 'Sending…' : 'Test on my WhatsApp'}</button>}
        {edit && <input className="fl-note" type="text" placeholder="What changed? (optional)" aria-label="Publish note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} />}
        {edit && <button className="cx-btn cx-btn-primary cx-btn-sm" onClick={publish} disabled={!dirty.size || !!busy}>{busy === 'publish' ? 'Publishing…' : 'Publish'}</button>}
      </div>
      {msg && <div className={msg.ok ? 'fl-msg' : 'cx-err cx-err-box'} role={msg.ok ? 'status' : 'alert'}>{msg.text}</div>}
      {showHistory && (
        <div className="fl-history">
          {data.versions.length === 0 ? <span className="cx-hint">Nothing published yet. Customers see the original texts.</span> : data.versions.map((v) => (
            <div key={v.version} className="fl-hrow">
              <b>v{v.version}</b><span>{when(v.publishedAtMs)} · {v.by || 'team'}{v.note ? ` · "${v.note}"` : ''}{v.changed?.length ? ` · ${v.changed.length} step${v.changed.length === 1 ? '' : 's'} changed` : ''}</span>
              {edit && v.version !== data.version && <button className="cx-linkbtn" onClick={() => revert(v.version)} disabled={!!busy}>Put back in draft</button>}
            </div>
          ))}
        </div>
      )}

      <div className="fl-main">
        <nav className="fl-flows" aria-label="Flows">
          {data.flows.map((f) => (
            <button key={f.id} aria-current={f.id === flow.id} onClick={() => { setFlowId(f.id); setStepId(null); }}>
              <span>{f.name}</span>
              {f.steps.some((s) => dirty.has(s.id)) && <i className="fl-dot" title="Unpublished changes" />}
            </button>
          ))}
        </nav>

        <div className="fl-canvas" aria-label="Flow chart">
          <div className="fl-cost"><span className="cx-chip cx-c-resolved">Free · replies in the chat</span>
            {FLOW_SWITCH[flow.id] && (
              <span className="fl-switch">
                <span>{FLOW_SWITCH_HINT[data.features?.[FLOW_SWITCH[flow.id]] || 'test']}</span>
                <span className="cx-seg" role="group" aria-label={`${flow.name}: who gets it`}>
                  {['off', 'test', 'live'].map((m) => <button key={m} aria-pressed={(data.features?.[FLOW_SWITCH[flow.id]] || 'test') === m} disabled={!edit || busy === 'mode'} onClick={() => setFlowMode(FLOW_SWITCH[flow.id], m)}>{m === 'off' ? 'Off' : m === 'test' ? 'Test' : 'Live'}</button>)}
                </span>
              </span>
            )}
          </div>
          <div className="fl-node trigger" role="note"><span className="fl-k">Starts when</span><span>{flow.starts}</span></div>
          {flow.steps.map((s) => {
            const v = nodes[s.id];
            const locked = !EDITABLE.has(s.kind);
            const u = usage(s);
            return (
              <React.Fragment key={s.id}>
                <span className="fl-line" aria-hidden="true" />
                <button className={`fl-node ${s.kind}${step?.id === s.id ? ' sel' : ''}`} onClick={() => setStepId(s.id)}>
                  <span className="fl-k">{KIND[s.kind]}{locked && <span className="fl-lock">fixed</span>}{dirty.has(s.id) && <span className="fl-edited">edited</span>}</span>
                  <b>{s.title}</b>
                  {locked ? <span className="fl-t">{s.text}</span>
                    : (s.kind === 'message' || s.kind === 'label') ? <span className="fl-t">{pick(v?.text, lang)}</span>
                      : <span className="fl-chips">{(v?.items || []).filter((i) => i.enabled !== false).map((i, n) => <span key={n}>{pick(i.title, lang)}</span>)}</span>}
                  {u ? <span className="fl-s">{u} in the last 14 days</span> : null}
                </button>
              </React.Fragment>
            );
          })}
          <span className="fl-line" aria-hidden="true" />
          <span className="fl-end">End · the chat goes to the right inbox tab</span>
        </div>

        <aside className="fl-editor" aria-label="Edit step">
          {!step ? null : !EDITABLE.has(step.kind) ? (
            <>
              <b className="fl-et">{step.title}</b>
              <p className="cx-hint">{step.text}</p>
              <p className="fl-locknote">{step.kind === 'decision' ? 'Decisions use your order data and privacy rules, so they can\'t be changed here. Change the messages on each branch instead.' : 'Actions save records and create tasks for the team. They work the same whatever the texts say.'}</p>
            </>
          ) : (
            <>
              <div className="fl-eh"><b className="fl-et">{step.title}</b>{edit && <button className="cx-linkbtn" onClick={resetStep}>Reset to original</button>}</div>
              <div className="cx-seg" role="group" aria-label="Language">{LANGS.map(([id, l]) => <button key={id} aria-pressed={lang === id} onClick={() => setLang(id)}>{l}</button>)}</div>
              {(step.kind === 'message' || step.kind === 'label') && (
                <>
                  <label className="fl-lbl" htmlFor="fl-text">{step.kind === 'label' ? 'Label' : 'Message'} <Counter value={value?.text?.[lang]} max={step.max || data.limits.text} /></label>
                  <textarea id="fl-text" rows={step.kind === 'label' ? 2 : 7} disabled={!edit} value={value?.text?.[lang] || ''} placeholder={lang === 'en' ? '' : 'Empty: customers writing this way get the English text.'}
                    onChange={(e) => change(step.id, { ...value, text: { ...value.text, [lang]: e.target.value } })} />
                  {step.vars?.length > 0 && <div className="fl-vars">{step.vars.map((t) => <button key={t} disabled={!edit} onClick={() => change(step.id, { ...value, text: { ...value.text, [lang]: `${value.text?.[lang] || ''}{${t}}` } })}>{`{${t}}`}</button>)}</div>}
                  {step.required?.length > 0 && <p className="cx-hint">Must keep {step.required.join(', ')}.</p>}
                </>
              )}
              {(step.kind === 'buttons' || step.kind === 'list') && (
                <>
                  {step.kind === 'list' && (
                    <>
                      <label className="fl-lbl" htmlFor="fl-menu">Menu button <Counter value={value?.menuButton?.[lang]} max={data.limits.button} /></label>
                      <input id="fl-menu" type="text" disabled={!edit} value={value?.menuButton?.[lang] || ''} onChange={(e) => change(step.id, { ...value, menuButton: { ...value.menuButton, [lang]: e.target.value } })} />
                    </>
                  )}
                  <span className="fl-lbl">{step.kind === 'list' ? 'Options (up to 10)' : 'Buttons (up to 3)'}</span>
                  <div className="fl-items">
                    {(value?.items || []).map((it, n) => {
                      const meta = step.items.find((m) => (m.key && m.key === it.key) || (m.code && m.code === it.code)) || {};
                      const max = step.kind === 'list' ? data.limits.row : data.limits.button;
                      const setItem = (patch) => change(step.id, { ...value, items: value.items.map((x, i) => (i === n ? { ...x, ...patch } : x)) });
                      const move = (d) => { const a = [...value.items]; const j = n + d; if (j < 0 || j >= a.length) return; [a[n], a[j]] = [a[j], a[n]]; change(step.id, { ...value, items: a }); };
                      return (
                        <div key={n} className={`fl-item${it.enabled === false ? ' off' : ''}`}>
                          <input type="checkbox" aria-label="Show this option" disabled={!edit || meta.fixed} checked={it.enabled !== false} onChange={(e) => setItem({ enabled: e.target.checked })} />
                          <input type="text" aria-label={`Option ${n + 1}`} disabled={!edit} value={it.title?.[lang] || ''} placeholder={lang === 'en' ? '' : pick(it.title, 'en')} onChange={(e) => setItem({ title: { ...it.title, [lang]: e.target.value } })} />
                          <Counter value={it.title?.[lang]} max={max} />
                          <span className="fl-go">{meta.goes ? `→ ${meta.goes}` : meta.fixed ? 'always shown' : it.code && !meta.code ? 'new reason' : ''}</span>
                          {edit && <span className="fl-move"><button aria-label="Move up" onClick={() => move(-1)} disabled={n === 0}>↑</button><button aria-label="Move down" onClick={() => move(1)} disabled={n === value.items.length - 1}>↓</button>
                            {step.kind === 'list' && !meta.code && <button aria-label="Remove" onClick={() => change(step.id, { ...value, items: value.items.filter((_, i) => i !== n) })}>×</button>}</span>}
                        </div>
                      );
                    })}
                  </div>
                  {edit && step.kind === 'list' && (value?.items || []).length < 10 && (
                    <button className="cx-btn cx-btn-sm" onClick={() => change(step.id, { ...value, items: [...value.items.filter((i) => i.code !== 'OTHER'), { code: `C_${Date.now().toString(36).toUpperCase()}`, title: { en: 'New reason', hinglish: '', hi: '' }, enabled: true }, ...value.items.filter((i) => i.code === 'OTHER')] })}>Add option</button>
                  )}
                  {step.kind === 'buttons' && <p className="cx-hint">Untick to hide a button. Buttons always lead to their own step, so new buttons can't be added here.</p>}
                </>
              )}
              {step.note && <p className="cx-hint">{step.note}</p>}
              <span className="fl-lbl">Preview on WhatsApp</span>
              <Preview step={step} value={value} lang={lang} />
            </>
          )}
        </aside>
      </div>
    </section>
  );
}

export default FlowsPage;
